import { useState, useEffect, useCallback, useRef } from 'react';
import { Layout } from '../components/Layout';
import { 
  Factory, Plus, Search, FileText, Download, ChevronRight, 
  CheckCircle, Clock, X, History, Package, AlertCircle, Trash2, Calendar, Filter
} from 'lucide-react';
import axios from 'axios';
import { toast } from 'sonner';
import { formatISTDate, formatISTDateTime } from '../utils/timezone';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PRODUCTION_STAGES = [
  { id: 'printing_embroidery', label: 'Printing / Embroidery' },
  { id: 'cutting', label: 'Cutting' },
  { id: 'stitching', label: 'Stitching' },
  { id: 'button_handwork', label: 'Button / Handwork' },
  { id: 'dhaga_cutting', label: 'Dhaga Cutting' },
  { id: 'final_checking', label: 'Final Checking / Repairing' },
  { id: 'ironing', label: 'Ironing' },
  { id: 'packing', label: 'Packing' },
];

const getStatusBadge = (status) => {
  if (status === 'completed') {
    return (
      <span className="px-2 py-1 text-xs font-medium rounded-full bg-emerald-50 text-emerald-600 border border-emerald-200">
        Completed
      </span>
    );
  }
  return (
    <span className="px-2 py-1 text-xs font-medium rounded-full bg-amber-50 text-amber-600 border border-amber-200">
      In Progress
    </span>
  );
};

const getStageBadge = (stage) => {
  const stageObj = PRODUCTION_STAGES.find(s => s.id === stage);
  return stageObj?.label || 'Not Started';
};

// Standard size order for consistent display
const SIZE_ORDER = ['S', 'M', 'L', 'XL', 'XXL'];

const sortSizes = (sizes) => {
  return [...sizes].sort((a, b) => {
    const aIdx = SIZE_ORDER.indexOf(a);
    const bIdx = SIZE_ORDER.indexOf(b);
    if (aIdx === -1 && bIdx === -1) return a.localeCompare(b);
    if (aIdx === -1) return 1;
    if (bIdx === -1) return -1;
    return aIdx - bIdx;
  });
};

// Job Card List Item Component
const JobCardItem = ({ jobCard, onView, onDownload, onDelete, isAdmin }) => (
  <div className="bg-white border border-slate-200 rounded-lg p-4 hover:shadow-md transition-shadow">
    <div className="flex justify-between items-start mb-3">
      <div>
        <h3 className="font-semibold text-slate-900">{jobCard.job_card_number}</h3>
        <p className="text-sm text-slate-500">{jobCard.label_name}</p>
      </div>
      {getStatusBadge(jobCard.status)}
    </div>
    
    <div className="text-xs text-slate-500 mb-3 space-y-1">
      <div className="flex items-center gap-1">
        <span className="font-medium text-slate-600">Designs:</span>
        <span className="text-slate-700">{jobCard.design_numbers?.join(', ') || 'N/A'}</span>
      </div>
      <div className="flex items-center gap-1">
        <span className="font-medium text-slate-600">Total Qty:</span>
        <span className="text-indigo-600 font-semibold">{jobCard.total_quantity || 0}</span>
      </div>
      <div>Stage: {getStageBadge(jobCard.current_stage)}</div>
      <div>Updated: {formatISTDate(jobCard.updated_at)}</div>
    </div>
    
    <div className="flex gap-2">
      <button
        onClick={() => onView(jobCard)}
        className="flex-1 px-3 py-2 text-sm bg-indigo-600 text-white rounded-md hover:bg-indigo-700 transition-colors"
      >
        View / Update
      </button>
      <button
        onClick={() => onDownload(jobCard.job_card_number)}
        className="px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200 transition-colors"
        title="Download PDF"
      >
        <Download className="w-4 h-4" />
      </button>
      {isAdmin && (
        <button
          onClick={() => onDelete(jobCard.job_card_number)}
          className="px-3 py-2 text-sm bg-rose-50 text-rose-600 rounded-md hover:bg-rose-100 transition-colors"
          title="Delete Job Card"
        >
          <X className="w-4 h-4" />
        </button>
      )}
    </div>
  </div>
);

export const ProductionTools = () => {
  const { user: currentUser } = useAuth();
  const isAdmin = currentUser?.role === 'admin';
  
  const [activeTab, setActiveTab] = useState('list'); // 'list', 'create', 'view'
  const [jobCards, setJobCards] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selectedJobCard, setSelectedJobCard] = useState(null);
  const [statusFilter, setStatusFilter] = useState('all');
  const [designNumberFilter, setDesignNumberFilter] = useState('');
  const [stageFilter, setStageFilter] = useState('all');
  const [fromDateFilter, setFromDateFilter] = useState('');
  const [toDateFilter, setToDateFilter] = useState('');
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deletingJobCard, setDeletingJobCard] = useState(null);
  const [completionError, setCompletionError] = useState('');
  
  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalJobCards, setTotalJobCards] = useState(0);
  const PER_PAGE = 50;

  // Create Job Card State
  const [productSearch, setProductSearch] = useState('');
  const [productSuggestions, setProductSuggestions] = useState([]);
  const [selectedProducts, setSelectedProducts] = useState([]);
  const [labelName, setLabelName] = useState('');
  const [instructions, setInstructions] = useState('');
  const [showProductDropdown, setShowProductDropdown] = useState(false);
  const [loadingProducts, setLoadingProducts] = useState(false);
  const [creating, setCreating] = useState(false);
  const dropdownRef = useRef(null);

  // Stage Update State
  const [selectedStage, setSelectedStage] = useState('');
  const [stageQuantities, setStageQuantities] = useState({});
  const [damageQuantities, setDamageQuantities] = useState({});
  const [damageReason, setDamageReason] = useState('');
  const [updatingStage, setUpdatingStage] = useState(false);
  const [showHistory, setShowHistory] = useState(false);

  // Fetch job cards
  const fetchJobCards = useCallback(async (page = 1) => {
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (statusFilter !== 'all') params.append('status_filter', statusFilter);
      if (designNumberFilter.trim()) params.append('design_number', designNumberFilter.trim());
      if (stageFilter !== 'all') params.append('stage', stageFilter);
      if (fromDateFilter) params.append('from_date', new Date(fromDateFilter).toISOString());
      if (toDateFilter) params.append('to_date', new Date(toDateFilter + 'T23:59:59').toISOString());
      params.append('page', page.toString());
      params.append('per_page', PER_PAGE.toString());
      
      const queryString = params.toString();
      const response = await axios.get(`${API}/job-cards${queryString ? `?${queryString}` : ''}`);
      
      // Handle paginated response
      const data = response.data;
      if (data.job_cards) {
        setJobCards(data.job_cards);
        setTotalPages(data.total_pages || 1);
        setTotalJobCards(data.total || 0);
        setCurrentPage(data.page || 1);
      } else {
        // Fallback for backward compatibility
        setJobCards(Array.isArray(data) ? data : []);
        setTotalPages(1);
        setTotalJobCards(Array.isArray(data) ? data.length : 0);
      }
    } catch (error) {
      toast.error('Failed to fetch job cards');
    } finally {
      setLoading(false);
    }
  }, [statusFilter, designNumberFilter, stageFilter, fromDateFilter, toDateFilter, PER_PAGE]);

  useEffect(() => {
    fetchJobCards(currentPage);
  }, [fetchJobCards, currentPage]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [statusFilter, designNumberFilter, stageFilter, fromDateFilter, toDateFilter]);

  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
        setShowProductDropdown(false);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  // Search products
  const searchProducts = useCallback(async (search) => {
    if (!search.trim()) {
      setProductSuggestions([]);
      return;
    }
    setLoadingProducts(true);
    try {
      const response = await axios.get(`${API}/job-cards/products/search?search=${encodeURIComponent(search)}`);
      const filtered = response.data.filter(
        p => !selectedProducts.some(sp => sp.design_number === p.design_number)
      );
      setProductSuggestions(filtered);
    } catch (error) {
      console.error('Failed to search products:', error);
    } finally {
      setLoadingProducts(false);
    }
  }, [selectedProducts]);

  useEffect(() => {
    const timer = setTimeout(() => {
      if (productSearch) searchProducts(productSearch);
    }, 300);
    return () => clearTimeout(timer);
  }, [productSearch, searchProducts]);

  // Add product to selection
  const addProduct = (product) => {
    const sizeQuantities = {};
    product.available_sizes.forEach(size => { sizeQuantities[size] = 0; });
    
    setSelectedProducts(prev => [...prev, {
      ...product,
      size_quantities: sizeQuantities
    }]);
    setProductSearch('');
    setProductSuggestions([]);
    setShowProductDropdown(false);
  };

  // Remove product from selection
  const removeProduct = (designNumber) => {
    setSelectedProducts(prev => prev.filter(p => p.design_number !== designNumber));
  };

  // Update product quantity
  const updateProductQuantity = (designNumber, size, value) => {
    setSelectedProducts(prev => prev.map(p => {
      if (p.design_number === designNumber) {
        return {
          ...p,
          size_quantities: {
            ...p.size_quantities,
            [size]: parseInt(value) || 0
          }
        };
      }
      return p;
    }));
  };

  // Create job card
  const handleCreateJobCard = async () => {
    if (selectedProducts.length === 0) {
      toast.error('Please add at least one product');
      return;
    }
    if (!labelName.trim()) {
      toast.error('Please enter a label name');
      return;
    }

    // Check if at least one product has quantities
    const hasQuantities = selectedProducts.some(p => 
      Object.values(p.size_quantities).some(qty => qty > 0)
    );
    if (!hasQuantities) {
      toast.error('Please enter quantities for at least one product');
      return;
    }

    setCreating(true);
    try {
      const payload = {
        products: selectedProducts.map(p => ({
          design_number: p.design_number,
          product_name: p.product_name,
          primary_image: p.primary_image,
          size_quantities: p.size_quantities
        })),
        label_name: labelName,
        instructions: instructions
      };

      const response = await axios.post(`${API}/job-cards`, payload);
      toast.success(`Job Card ${response.data.job_card_number} created successfully!`);
      
      // Reset form
      setSelectedProducts([]);
      setLabelName('');
      setInstructions('');
      setActiveTab('list');
      setCurrentPage(1); // Reset to first page to see the new job card
      fetchJobCards(1);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create job card');
    } finally {
      setCreating(false);
    }
  };

  // View job card - fetch full details including history
  const handleViewJobCard = async (jobCard) => {
    setSelectedStage('');
    setStageQuantities({});
    setDamageQuantities({});
    setDamageReason('');
    setShowHistory(false);
    setActiveTab('view');
    
    // Fetch full job card details including history
    try {
      const response = await axios.get(`${API}/job-cards/${jobCard.job_card_number}`);
      setSelectedJobCard(response.data);
    } catch (error) {
      console.error('Failed to fetch job card details:', error);
      // Fallback to the list data
      setSelectedJobCard(jobCard);
      toast.error('Could not load full history');
    }
  };

  // Download PDF
  const handleDownloadPDF = async (jobCardNumber) => {
    try {
      const response = await axios.get(`${API}/job-cards/${jobCardNumber}/pdf`, {
        responseType: 'blob'
      });
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', `JobCard_${jobCardNumber}.pdf`);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (error) {
      toast.error('Failed to download PDF');
    }
  };

  // Delete job card (Admin only)
  const handleDeleteJobCard = async () => {
    if (!deletingJobCard) return;
    
    try {
      await axios.delete(`${API}/job-cards/${deletingJobCard}`);
      toast.success(`Job Card ${deletingJobCard} deleted successfully`);
      setShowDeleteModal(false);
      setDeletingJobCard(null);
      fetchJobCards(currentPage);
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to delete job card');
    }
  };

  const openDeleteModal = (jobCardNumber) => {
    setDeletingJobCard(jobCardNumber);
    setShowDeleteModal(true);
  };

  // Reset filters
  const resetFilters = () => {
    setStatusFilter('all');
    setDesignNumberFilter('');
    setStageFilter('all');
    setFromDateFilter('');
    setToDateFilter('');
    setCurrentPage(1); // Reset to first page when filters reset
  };

  const hasActiveFilters = statusFilter !== 'all' || designNumberFilter.trim() || stageFilter !== 'all' || fromDateFilter || toDateFilter;

  // Initialize stage quantities when stage is selected
  const handleStageSelect = (stage) => {
    setSelectedStage(stage);
    if (selectedJobCard) {
      const product = selectedJobCard.products[0];
      const sizes = Object.keys(product.initial_quantities);
      const newQty = {};
      const newDmg = {};
      const stageData = product.stage_quantities?.[stage] || { processed: {}, damaged: {} };
      sizes.forEach(size => {
        newQty[size] = stageData.processed?.[size] || 0;
        newDmg[size] = stageData.damaged?.[size] || 0;
      });
      setStageQuantities(newQty);
      setDamageQuantities(newDmg);
    }
  };

  // Update stage quantities
  const handleUpdateStage = async () => {
    if (!selectedStage) {
      toast.error('Please select a stage');
      return;
    }

    setUpdatingStage(true);
    try {
      const payload = {
        stage: selectedStage,
        size_quantities: stageQuantities,
        damage_quantities: Object.values(damageQuantities).some(v => v > 0) ? damageQuantities : null,
        damage_reason: damageReason || null
      };

      const response = await axios.put(
        `${API}/job-cards/${selectedJobCard.job_card_number}/stage`,
        payload
      );
      
      toast.success('Stage updated successfully!');
      setSelectedJobCard(response.data);
      setDamageReason('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update stage');
    } finally {
      setUpdatingStage(false);
    }
  };

  // Complete job card
  const handleCompleteJobCard = async () => {
    if (!window.confirm('Are you sure you want to mark this job card as completed? This validates that Total Packed + Total Damaged = Total Initial for all sizes.')) return;

    setCompletionError('');
    try {
      const response = await axios.put(`${API}/job-cards/${selectedJobCard.job_card_number}/complete`);
      toast.success('Job card marked as completed!');
      setSelectedJobCard(response.data);
      fetchJobCards();
    } catch (error) {
      const errorMsg = error.response?.data?.detail || 'Failed to complete job card';
      setCompletionError(errorMsg);
      toast.error('Cannot complete job card - see validation errors below');
    }
  };

  // Render Create Job Card Form
  const renderCreateForm = () => (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <h2 className="text-xl font-semibold text-slate-900">Create New Job Card</h2>
        <button
          onClick={() => setActiveTab('list')}
          className="text-slate-500 hover:text-slate-700"
        >
          <X className="w-5 h-5" />
        </button>
      </div>

      {/* Label Name */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Label Name *</label>
        <input
          type="text"
          value={labelName}
          onChange={(e) => setLabelName(e.target.value)}
          placeholder="Enter label name"
          className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Product Search */}
      <div ref={dropdownRef}>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Add Products *</label>
        <div className="relative">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
          <input
            type="text"
            value={productSearch}
            onChange={(e) => {
              setProductSearch(e.target.value);
              setShowProductDropdown(true);
            }}
            onFocus={() => setShowProductDropdown(true)}
            placeholder="Search by design number..."
            className="w-full pl-10 pr-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
          />
          
          {showProductDropdown && productSearch && (
            <div className="absolute z-10 w-full mt-1 bg-white border border-slate-200 rounded-md shadow-lg max-h-60 overflow-y-auto">
              {loadingProducts ? (
                <div className="px-4 py-3 text-sm text-slate-500">Searching...</div>
              ) : productSuggestions.length === 0 ? (
                <div className="px-4 py-3 text-sm text-slate-500">No products found</div>
              ) : (
                productSuggestions.map((product) => (
                  <button
                    key={product.design_number}
                    type="button"
                    onClick={() => addProduct(product)}
                    className="w-full px-4 py-3 text-left hover:bg-slate-50 flex items-center gap-3 border-b border-slate-100 last:border-b-0"
                  >
                    <img src={product.primary_image} alt="" className="w-10 h-10 object-cover rounded" />
                    <div>
                      <div className="font-medium text-slate-900">{product.design_number}</div>
                      <div className="text-sm text-slate-500">{product.product_name}</div>
                      <div className="text-xs text-slate-400">Sizes: {product.available_sizes.join(', ')}</div>
                    </div>
                  </button>
                ))
              )}
            </div>
          )}
        </div>
      </div>

      {/* Selected Products */}
      {selectedProducts.length > 0 && (
        <div className="space-y-4">
          <h3 className="text-sm font-medium text-slate-700">Selected Products ({selectedProducts.length})</h3>
          {selectedProducts.map((product) => (
            <div key={product.design_number} className="border border-slate-200 rounded-lg p-4">
              <div className="flex justify-between items-start mb-3">
                <div className="flex gap-3">
                  <img src={product.primary_image} alt="" className="w-12 h-12 object-cover rounded" />
                  <div>
                    <div className="font-medium text-slate-900">{product.design_number}</div>
                    <div className="text-sm text-slate-500">{product.product_name}</div>
                  </div>
                </div>
                <button
                  onClick={() => removeProduct(product.design_number)}
                  className="text-rose-600 hover:bg-rose-50 p-1 rounded"
                >
                  <X className="w-4 h-4" />
                </button>
              </div>
              
              <div className="grid grid-cols-3 sm:grid-cols-5 gap-2">
                {sortSizes(product.available_sizes).map((size) => (
                  <div key={size}>
                    <label className="block text-xs text-slate-500 mb-1">{size}</label>
                    <input
                      type="number"
                      min="0"
                      value={product.size_quantities[size]}
                      onChange={(e) => updateProductQuantity(product.design_number, size, e.target.value)}
                      className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                    />
                  </div>
                ))}
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Instructions */}
      <div>
        <label className="block text-sm font-medium text-slate-700 mb-1.5">Instructions / Notes</label>
        <textarea
          value={instructions}
          onChange={(e) => setInstructions(e.target.value)}
          rows={4}
          placeholder="Enter any special instructions..."
          className="w-full px-4 py-2.5 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
        />
      </div>

      {/* Submit Button */}
      <button
        onClick={handleCreateJobCard}
        disabled={creating}
        className="w-full px-4 py-3 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all disabled:opacity-50"
      >
        {creating ? 'Creating...' : 'Create Job Card'}
      </button>
    </div>
  );

  // Render Job Card View/Update
  const renderJobCardView = () => {
    if (!selectedJobCard) return null;
    const product = selectedJobCard.products[0];
    const sizes = sortSizes(Object.keys(product.initial_quantities));

    return (
      <div className="space-y-6">
        <div className="flex items-center justify-between">
          <div>
            <h2 className="text-xl font-semibold text-slate-900">{selectedJobCard.job_card_number}</h2>
            <p className="text-sm text-slate-500">{selectedJobCard.label_name}</p>
          </div>
          <div className="flex items-center gap-2">
            {getStatusBadge(selectedJobCard.status)}
            <button
              onClick={() => setActiveTab('list')}
              className="text-slate-500 hover:text-slate-700 p-1"
            >
              <X className="w-5 h-5" />
            </button>
          </div>
        </div>

        {/* Actions */}
        <div className="flex flex-wrap gap-2">
          <button
            onClick={() => handleDownloadPDF(selectedJobCard.job_card_number)}
            className="inline-flex items-center px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200"
          >
            <Download className="w-4 h-4 mr-1.5" /> Download PDF
          </button>
          <button
            onClick={() => setShowHistory(!showHistory)}
            className="inline-flex items-center px-3 py-2 text-sm bg-slate-100 text-slate-700 rounded-md hover:bg-slate-200"
          >
            <History className="w-4 h-4 mr-1.5" /> {showHistory ? 'Hide History' : 'View History'}
          </button>
          {selectedJobCard.status !== 'completed' && (
            <button
              onClick={handleCompleteJobCard}
              className="inline-flex items-center px-3 py-2 text-sm bg-emerald-600 text-white rounded-md hover:bg-emerald-700"
            >
              <CheckCircle className="w-4 h-4 mr-1.5" /> Mark Complete
            </button>
          )}
        </div>

        {/* Completion Validation Error */}
        {completionError && (
          <div className="bg-rose-50 border border-rose-200 rounded-lg p-4">
            <div className="flex items-start gap-2">
              <AlertCircle className="w-5 h-5 text-rose-600 mt-0.5 flex-shrink-0" />
              <div>
                <h4 className="font-medium text-rose-800 mb-1">Cannot Complete Job Card</h4>
                <p className="text-sm text-rose-700 whitespace-pre-wrap">{completionError}</p>
                <p className="text-xs text-rose-600 mt-2">
                  Rule: Total Packed Qty + Total Damaged Qty must equal Total Initial Qty for all sizes before marking as completed.
                </p>
              </div>
            </div>
            <button
              onClick={() => setCompletionError('')}
              className="mt-3 text-sm text-rose-600 hover:text-rose-800"
            >
              Dismiss
            </button>
          </div>
        )}

        {/* History Modal */}
        {showHistory && (
          <div className="bg-slate-50 border border-slate-200 rounded-lg p-4 max-h-96 overflow-y-auto">
            <h3 className="font-medium text-slate-900 mb-3 flex items-center gap-2">
              <History className="w-4 h-4" />
              Complete History Log
            </h3>
            
            {(!selectedJobCard.history || selectedJobCard.history.length === 0) ? (
              <div className="text-center py-6 text-slate-500">
                <Clock className="w-8 h-8 mx-auto mb-2 text-slate-300" />
                <p className="text-sm">No updates yet</p>
                <p className="text-xs text-slate-400 mt-1">History will appear as stages are updated</p>
              </div>
            ) : (
              <div className="space-y-4">
                {/* Sort history newest first */}
                {[...selectedJobCard.history].reverse().map((entry, index) => {
                  const isStageUpdate = entry.action === 'stage_update';
                  const isCreated = entry.action === 'created';
                  const isCompleted = entry.action === 'completed';
                  
                  // Calculate total quantities for this update
                  const totalProcessed = entry.quantities 
                    ? Object.values(entry.quantities).reduce((sum, qty) => sum + (qty || 0), 0) 
                    : 0;
                  const totalDamaged = entry.damage_quantities 
                    ? Object.values(entry.damage_quantities).reduce((sum, qty) => sum + (qty || 0), 0) 
                    : 0;
                  
                  return (
                    <div 
                      key={index} 
                      className={`rounded-lg border p-3 ${
                        isCompleted ? 'border-emerald-200 bg-emerald-50' :
                        isCreated ? 'border-blue-200 bg-blue-50' :
                        'border-slate-200 bg-white'
                      }`}
                    >
                      {/* Header Row: Date, Action, User */}
                      <div className="flex flex-wrap items-start justify-between gap-2 mb-2">
                        <div>
                          <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${
                            isCompleted ? 'bg-emerald-100 text-emerald-700' :
                            isCreated ? 'bg-blue-100 text-blue-700' :
                            'bg-indigo-100 text-indigo-700'
                          }`}>
                            {isStageUpdate ? entry.stage_label || entry.stage?.replace(/_/g, ' ') : 
                             isCreated ? 'Created' : 
                             isCompleted ? 'Completed' : 
                             entry.action?.replace(/_/g, ' ')}
                          </span>
                        </div>
                        <div className="text-xs text-slate-500 text-right">
                          <div className="font-medium">{entry.formatted_date || formatISTDateTime(entry.timestamp)}</div>
                          <div className="capitalize">
                            {entry.user} 
                            {entry.user_role && <span className="text-slate-400"> ({entry.user_role})</span>}
                          </div>
                        </div>
                      </div>
                      
                      {/* Stage Update Details */}
                      {isStageUpdate && (
                        <div className="space-y-2">
                          {/* Size-wise Quantities */}
                          {entry.quantities && Object.keys(entry.quantities).length > 0 && (
                            <div>
                              <div className="text-xs font-medium text-slate-600 mb-1">Quantities Updated:</div>
                              <div className="flex flex-wrap gap-2">
                                {sortSizes(Object.keys(entry.quantities))
                                  .filter(size => entry.quantities[size] > 0)
                                  .map(size => (
                                    <span key={size} className="inline-flex items-center px-2 py-1 bg-slate-100 rounded text-xs">
                                      <span className="font-medium text-slate-700">{size}:</span>
                                      <span className="ml-1 text-indigo-600 font-semibold">{entry.quantities[size]}</span>
                                    </span>
                                  ))}
                              </div>
                              <div className="text-xs text-slate-500 mt-1">
                                Total moved: <span className="font-medium text-indigo-600">{totalProcessed}</span>
                              </div>
                            </div>
                          )}
                          
                          {/* Damage Quantities */}
                          {entry.damage_quantities && totalDamaged > 0 && (
                            <div className="mt-2 pt-2 border-t border-slate-200">
                              <div className="text-xs font-medium text-rose-600 mb-1">Damaged Items:</div>
                              <div className="flex flex-wrap gap-2">
                                {sortSizes(Object.keys(entry.damage_quantities))
                                  .filter(size => entry.damage_quantities[size] > 0)
                                  .map(size => (
                                    <span key={size} className="inline-flex items-center px-2 py-1 bg-rose-50 rounded text-xs">
                                      <span className="font-medium text-rose-700">{size}:</span>
                                      <span className="ml-1 text-rose-600 font-semibold">−{entry.damage_quantities[size]}</span>
                                    </span>
                                  ))}
                              </div>
                              <div className="text-xs text-rose-500 mt-1">
                                Total damaged: <span className="font-medium">{totalDamaged}</span>
                              </div>
                              {entry.damage_reason && (
                                <div className="text-xs text-rose-600 mt-1 italic">
                                  Reason: "{entry.damage_reason}"
                                </div>
                              )}
                            </div>
                          )}
                        </div>
                      )}
                      
                      {/* Created Details */}
                      {isCreated && entry.details && (
                        <div className="text-sm text-slate-600">{entry.details}</div>
                      )}
                      
                      {/* Completed Details */}
                      {isCompleted && (
                        <div className="text-sm text-emerald-700 font-medium">
                          Job card marked as completed
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        )}

        {/* Product Info */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-900 mb-3">Product Details</h3>
          {selectedJobCard.products.map((prod) => {
            const sortedSizes = sortSizes(Object.keys(prod.initial_quantities));
            return (
              <div key={prod.design_number} className="flex gap-3 mb-4 last:mb-0">
                <img src={prod.primary_image} alt="" className="w-16 h-16 object-cover rounded" />
                <div>
                  <div className="font-medium text-slate-900">{prod.design_number}</div>
                  <div className="text-sm text-slate-500">{prod.product_name}</div>
                  <div className="text-xs text-slate-400 mt-1">
                    Initial: {sortedSizes.map(s => `${s}:${prod.initial_quantities[s]}`).join(' | ')}
                  </div>
                </div>
              </div>
            );
          })}
          {selectedJobCard.instructions && (
            <div className="mt-3 pt-3 border-t border-slate-200">
              <div className="text-xs font-medium text-slate-500 mb-1">Instructions:</div>
              <div className="text-sm text-slate-700">{selectedJobCard.instructions}</div>
            </div>
          )}
        </div>

        {/* Stage Tracking */}
        {selectedJobCard.status !== 'completed' && (
          <div className="bg-white border border-slate-200 rounded-lg p-4">
            <h3 className="font-medium text-slate-900 mb-4">Update Production Stage</h3>
            
            {/* Stage Selection */}
            <div className="grid grid-cols-2 sm:grid-cols-4 gap-2 mb-4">
              {PRODUCTION_STAGES.map((stage) => (
                <button
                  key={stage.id}
                  onClick={() => handleStageSelect(stage.id)}
                  className={`px-3 py-2 text-xs font-medium rounded-md border transition-colors ${
                    selectedStage === stage.id
                      ? 'bg-indigo-600 text-white border-indigo-600'
                      : 'bg-white text-slate-700 border-slate-200 hover:border-indigo-300'
                  }`}
                >
                  {stage.label}
                </button>
              ))}
            </div>

            {/* Quantity Inputs */}
            {selectedStage && (
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Processed Quantities</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {sizes.map((size) => (
                      <div key={size}>
                        <label className="block text-xs text-slate-500 mb-1">{size}</label>
                        <input
                          type="number"
                          min="0"
                          value={stageQuantities[size] || 0}
                          onChange={(e) => setStageQuantities(prev => ({
                            ...prev,
                            [size]: parseInt(e.target.value) || 0
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-slate-200 rounded focus:outline-none focus:ring-2 focus:ring-indigo-500"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-2">Damaged Quantities (Optional)</label>
                  <div className="grid grid-cols-3 sm:grid-cols-6 gap-2">
                    {sizes.map((size) => (
                      <div key={size}>
                        <label className="block text-xs text-slate-500 mb-1">{size}</label>
                        <input
                          type="number"
                          min="0"
                          value={damageQuantities[size] || 0}
                          onChange={(e) => setDamageQuantities(prev => ({
                            ...prev,
                            [size]: parseInt(e.target.value) || 0
                          }))}
                          className="w-full px-2 py-1.5 text-sm border border-rose-200 rounded focus:outline-none focus:ring-2 focus:ring-rose-500 bg-rose-50"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div>
                  <label className="block text-sm font-medium text-slate-700 mb-1.5">Damage Reason (if any)</label>
                  <input
                    type="text"
                    value={damageReason}
                    onChange={(e) => setDamageReason(e.target.value)}
                    placeholder="e.g., Loose stitch, Fabric tear..."
                    className="w-full px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  />
                </div>

                <button
                  onClick={handleUpdateStage}
                  disabled={updatingStage}
                  className="w-full px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all disabled:opacity-50"
                >
                  {updatingStage ? 'Updating...' : 'Update Stage'}
                </button>
              </div>
            )}
          </div>
        )}

        {/* Stage Overview */}
        <div className="bg-white border border-slate-200 rounded-lg p-4">
          <h3 className="font-medium text-slate-900 mb-4">Stage Overview</h3>
          <div className="space-y-3">
            {PRODUCTION_STAGES.map((stage) => {
              const stageData = product.stage_quantities?.[stage.id] || { processed: {}, damaged: {} };
              const hasData = Object.values(stageData?.processed || {}).some(v => v > 0);
              const hasDamage = Object.values(stageData?.damaged || {}).some(v => v > 0);
              
              return (
                <div
                  key={stage.id}
                  className={`p-3 rounded-lg border ${
                    selectedJobCard.current_stage === stage.id
                      ? 'border-indigo-300 bg-indigo-50'
                      : hasData
                      ? 'border-emerald-200 bg-emerald-50'
                      : 'border-slate-200 bg-slate-50'
                  }`}
                >
                  <div className="flex justify-between items-center mb-2">
                    <span className="font-medium text-sm text-slate-900">{stage.label}</span>
                    {selectedJobCard.current_stage === stage.id && (
                      <span className="text-xs bg-indigo-600 text-white px-2 py-0.5 rounded">Current</span>
                    )}
                  </div>
                  {hasData ? (
                    <div className="text-xs text-slate-600">
                      {sortSizes(Object.keys(stageData.processed))
                        .filter(size => stageData.processed[size] > 0)
                        .map(size => `${size}: ${stageData.processed[size]}`)
                        .join(' | ')}
                    </div>
                  ) : (
                    <div className="text-xs text-slate-400">No quantities yet</div>
                  )}
                  {hasDamage && (
                    <div className="text-xs text-rose-600 mt-1">
                      Damaged: {sortSizes(Object.keys(stageData.damaged))
                        .filter(size => stageData.damaged[size] > 0)
                        .map(size => `${size}: ${stageData.damaged[size]}`)
                        .join(' | ')}
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Final Quantities (if completed) */}
        {selectedJobCard.status === 'completed' && selectedJobCard.final_quantities && (
          <div className="bg-emerald-50 border border-emerald-200 rounded-lg p-4">
            <h3 className="font-medium text-emerald-800 mb-3 flex items-center gap-2">
              <CheckCircle className="w-5 h-5" /> Final Completed Quantities
            </h3>
            {Object.entries(selectedJobCard.final_quantities).map(([design, quantities]) => (
              <div key={design} className="text-sm text-emerald-700">
                <span className="font-medium">{design}:</span>{' '}
                {sortSizes(Object.keys(quantities)).map(size => `${size}: ${quantities[size]}`).join(' | ')}
              </div>
            ))}
          </div>
        )}
      </div>
    );
  };

  return (
    <Layout>
      <div className="space-y-4 sm:space-y-6">
        {/* Header */}
        <div className="flex flex-col sm:flex-row sm:justify-between sm:items-center gap-4">
          <div>
            <h1 className="text-2xl sm:text-3xl lg:text-4xl font-bold text-slate-900 tracking-tight" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Production Tools
            </h1>
            <p className="mt-1 text-sm sm:text-base lg:text-lg text-slate-600" style={{ fontFamily: 'Public Sans, sans-serif' }}>
              Manage job cards and track production stages
            </p>
          </div>
          {activeTab === 'list' && (
            <button
              onClick={() => setActiveTab('create')}
              className="inline-flex items-center justify-center w-full sm:w-auto px-4 py-2.5 bg-indigo-600 text-white rounded-md hover:bg-indigo-700 font-medium transition-all active:scale-[0.98]"
            >
              <Plus className="w-4 h-4 mr-2" />
              New Job Card
            </button>
          )}
        </div>

        {/* Content */}
        <div className="bg-white border border-slate-200 rounded-lg shadow-sm p-4 sm:p-6">
          {activeTab === 'list' && (
            <>
              {/* Filters */}
              <div className="space-y-4 mb-6">
                <div className="flex flex-wrap items-center gap-3">
                  {/* Status Filter */}
                  <select
                    value={statusFilter}
                    onChange={(e) => setStatusFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="all">All Status</option>
                    <option value="in_progress">In Progress</option>
                    <option value="completed">Completed</option>
                  </select>

                  {/* Stage Filter */}
                  <select
                    value={stageFilter}
                    onChange={(e) => setStageFilter(e.target.value)}
                    className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                  >
                    <option value="all">All Stages</option>
                    {PRODUCTION_STAGES.map(stage => (
                      <option key={stage.id} value={stage.id}>{stage.label}</option>
                    ))}
                  </select>

                  {/* Design Number Search */}
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-slate-400" />
                    <input
                      type="text"
                      placeholder="Search design number..."
                      value={designNumberFilter}
                      onChange={(e) => setDesignNumberFilter(e.target.value)}
                      className="pl-9 pr-4 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm w-48"
                    />
                  </div>
                </div>

                {/* Date Range Filters */}
                <div className="flex flex-wrap items-center gap-3">
                  <div className="flex items-center gap-2">
                    <Calendar className="w-4 h-4 text-slate-400" />
                    <span className="text-sm text-slate-500">From:</span>
                    <input
                      type="date"
                      value={fromDateFilter}
                      onChange={(e) => setFromDateFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>
                  <div className="flex items-center gap-2">
                    <span className="text-sm text-slate-500">To:</span>
                    <input
                      type="date"
                      value={toDateFilter}
                      onChange={(e) => setToDateFilter(e.target.value)}
                      className="px-3 py-2 border border-slate-200 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500 text-sm"
                    />
                  </div>

                  {/* Reset Filters */}
                  {hasActiveFilters && (
                    <button
                      onClick={resetFilters}
                      className="inline-flex items-center px-3 py-2 text-sm text-indigo-600 hover:text-indigo-800 hover:bg-indigo-50 rounded-md transition-colors"
                    >
                      <X className="w-4 h-4 mr-1" />
                      Reset Filters
                    </button>
                  )}
                </div>

                {/* Results Count */}
                <div className="text-sm text-slate-500">
                  {totalJobCards > 0 
                    ? `Showing ${((currentPage - 1) * PER_PAGE) + 1}-${Math.min(currentPage * PER_PAGE, totalJobCards)} of ${totalJobCards} job card(s)` 
                    : '0 job cards found'}
                </div>
              </div>

              {/* Job Cards Grid */}
              {loading ? (
                <div className="text-center py-12">
                  <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-indigo-600 mx-auto"></div>
                  <p className="mt-4 text-slate-500">Loading job cards...</p>
                </div>
              ) : jobCards.length === 0 ? (
                <div className="text-center py-12">
                  <Factory className="w-12 h-12 text-slate-300 mx-auto mb-3" />
                  <p className="text-slate-600 font-medium">No job cards found</p>
                  <p className="text-sm text-slate-500 mt-1">Create your first job card to get started</p>
                </div>
              ) : (
                <>
                  <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4">
                    {jobCards.map((jobCard) => (
                      <JobCardItem
                        key={jobCard.job_card_number}
                        jobCard={jobCard}
                        onView={handleViewJobCard}
                        onDownload={handleDownloadPDF}
                        onDelete={openDeleteModal}
                        isAdmin={isAdmin}
                      />
                    ))}
                  </div>
                  
                  {/* Pagination Controls */}
                  {totalPages > 1 && (
                    <div className="flex flex-col sm:flex-row items-center justify-between gap-4 mt-6 px-4 py-3 bg-white border border-slate-200 rounded-lg">
                      <div className="text-sm text-slate-600">
                        Page {currentPage} of {totalPages}
                      </div>
                      <div className="flex items-center gap-1">
                        <button
                          onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
                          disabled={currentPage === 1}
                          className="px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Previous
                        </button>
                        
                        {/* Page numbers */}
                        {(() => {
                          const pages = [];
                          const maxVisible = 5;
                          let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
                          let end = Math.min(totalPages, start + maxVisible - 1);
                          if (end - start + 1 < maxVisible) {
                            start = Math.max(1, end - maxVisible + 1);
                          }
                          
                          if (start > 1) {
                            pages.push(
                              <button key={1} onClick={() => setCurrentPage(1)} className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm">1</button>
                            );
                            if (start > 2) pages.push(<span key="dots1" className="px-2 text-slate-400">...</span>);
                          }
                          
                          for (let i = start; i <= end; i++) {
                            pages.push(
                              <button
                                key={i}
                                onClick={() => setCurrentPage(i)}
                                className={`px-3 py-1 rounded-md text-sm ${i === currentPage ? 'bg-indigo-600 text-white' : 'hover:bg-slate-100'}`}
                              >
                                {i}
                              </button>
                            );
                          }
                          
                          if (end < totalPages) {
                            if (end < totalPages - 1) pages.push(<span key="dots2" className="px-2 text-slate-400">...</span>);
                            pages.push(
                              <button key={totalPages} onClick={() => setCurrentPage(totalPages)} className="px-3 py-1 rounded-md hover:bg-slate-100 text-sm">{totalPages}</button>
                            );
                          }
                          
                          return pages;
                        })()}
                        
                        <button
                          onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
                          disabled={currentPage === totalPages}
                          className="px-3 py-1.5 rounded-md border border-slate-200 hover:bg-slate-50 disabled:opacity-50 disabled:cursor-not-allowed text-sm"
                        >
                          Next
                        </button>
                      </div>
                    </div>
                  )}
                </>
              )}
            </>
          )}

          {activeTab === 'create' && renderCreateForm()}
          {activeTab === 'view' && renderJobCardView()}
        </div>

        {/* Delete Confirmation Modal - Admin Only */}
        {showDeleteModal && deletingJobCard && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={() => setShowDeleteModal(false)}>
            <div className="bg-white rounded-lg shadow-xl w-full max-w-md" onClick={(e) => e.stopPropagation()}>
              <div className="p-6">
                <div className="flex items-center justify-center w-12 h-12 mx-auto mb-4 rounded-full bg-rose-100">
                  <Trash2 className="w-6 h-6 text-rose-600" />
                </div>
                <h2 className="text-xl font-bold text-slate-900 text-center mb-2">Delete Job Card</h2>
                <p className="text-slate-600 text-center mb-4">
                  Are you sure you want to delete <strong>{deletingJobCard}</strong>?
                </p>
                <p className="text-rose-600 text-center text-sm mb-6">
                  This action cannot be undone.
                </p>
                <div className="flex gap-3">
                  <button
                    onClick={() => setShowDeleteModal(false)}
                    className="flex-1 px-4 py-2 bg-white border border-slate-200 text-slate-900 rounded-md hover:bg-slate-50 font-medium transition-colors"
                  >
                    Cancel
                  </button>
                  <button
                    onClick={handleDeleteJobCard}
                    className="flex-1 px-4 py-2 bg-rose-600 text-white rounded-md hover:bg-rose-700 font-medium transition-all"
                  >
                    Delete
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </Layout>
  );
};
